import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Upload, Sparkles, Trash2, AlertTriangle, Save, MessageCircle, X, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const UNITS = ["kg", "gm", "L", "ml", "pcs", "pack", "dozen", "bag", "box", "bundle"];

// Each item = { id, file_name, status: 'reading'|'ready'|'error'|'created', engine, rows, warnings, customer, bill_id?, error?, collapsed? }
export default function WhatsAppInbox() {
  const { t, user, lang } = useApp();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [items, setItems] = useState([]);

  if (user?.role !== "owner") {
    return <Card className="p-8 text-center text-stone-500" data-testid="wa-owner-only">Owner only</Card>;
  }

  const patch = (id, p) => setItems((prev) => prev.map((x) => x.id === id ? { ...x, ...p } : x));

  const readOne = async (item, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/photo-to-bill/read", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      patch(item.id, {
        status: "ready", engine: r.data.engine,
        rows: (r.data.rows || []).map((row, idx) => ({ ...row, rate: row.rate || 0, _rid: `${item.id}-${idx}-${Math.random().toString(36).slice(2, 6)}` })),
        warnings: r.data.warnings || [],
      });
    } catch (err) {
      patch(item.id, { status: "error", error: err?.response?.data?.detail || "AI read failed" });
    }
  };

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    // Queue all as reading, then start each read
    const newItems = files.map((f) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file_name: f.name || "photo",
      thumb_url: f.type?.startsWith("image/") ? URL.createObjectURL(f) : null,
      status: "reading", rows: [], warnings: [], customer: "", collapsed: false,
    }));
    setItems((prev) => [...newItems, ...prev]);
    // Parallel reads (Gemini handles concurrency fine)
    newItems.forEach((item, idx) => readOne(item, files[idx]));
  };

  const removeItem = (id) => setItems((prev) => {
    const rm = prev.find((x) => x.id === id);
    if (rm?.thumb_url) URL.revokeObjectURL(rm.thumb_url);
    return prev.filter((x) => x.id !== id);
  });
  const setRowIn = (id, rid, patchRow) => setItems((prev) => prev.map((x) => x.id === id ? { ...x, rows: x.rows.map((r) => r._rid === rid ? { ...r, ...patchRow } : r) } : x));
  const removeRow = (id, rid) => setItems((prev) => prev.map((x) => x.id === id ? { ...x, rows: x.rows.filter((r) => r._rid !== rid) } : x));

  const createDraft = async (item) => {
    const active = item.rows.filter((r) => !r.cancelled && (r.name_en || r.name_gu) && Number(r.quantity) > 0);
    if (active.length === 0) return toast.error(t("incomplete_row"));
    try {
      const lines = active.map((r) => ({
        item_id: r.matched_item_id || null,
        name_en: r.name_en || r.name_gu,
        name_gu: r.name_gu || "",
        unit: r.unit || "pcs",
        quantity: Number(r.quantity),
        rate: Number(r.rate) || 0,
        total: Number(r.quantity) * (Number(r.rate) || 0),
      }));
      const res = await api.post("/bills", {
        customer_name: item.customer, lines, discount: 0, amount_paid: 0, status: "credit",
        notes: `From WhatsApp inbox: ${item.file_name}`, language: lang,
      });
      patch(item.id, { status: "created", bill_id: res.data.id, collapsed: true });
      toast.success(t("draft_created"));
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const pendingCount = items.filter((x) => x.status === "reading").length;
  const readyCount = items.filter((x) => x.status === "ready").length;
  const createdCount = items.filter((x) => x.status === "created").length;

  return (
    <div className="space-y-5 pb-24">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#25D366] grid place-items-center">
              <MessageCircle className="w-4 h-4 text-white" />
            </div>
            <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("whatsapp_inbox")}</div>
          </div>
          <h1 className="font-heading text-3xl font-bold text-stone-900 mt-1">{t("wa_inbox_title")}</h1>
          <p className="text-stone-500 text-sm mt-1">{t("wa_inbox_help")}</p>
        </div>
        <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,image/*" multiple
               onChange={(e) => onFiles(e.target.files)} className="hidden" data-testid="wa-file-input" />
        <Button onClick={() => fileRef.current?.click()} data-testid="wa-upload-btn"
                className="h-12 sm:h-14 px-5 rounded-xl bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold whitespace-nowrap self-start sm:self-auto">
          <Upload className="w-4 h-4 mr-2" /> {t("wa_add_orders")}
        </Button>
      </div>

      {items.length === 0 && (
        <Card className="p-8 sm:p-10 rounded-2xl border-dashed border-2 border-stone-300 text-center" data-testid="wa-empty">
          <MessageCircle className="w-12 h-12 text-[#25D366] mx-auto mb-3" />
          <p className="text-stone-500 text-sm max-w-md mx-auto">{t("wa_empty_help")}</p>
        </Card>
      )}

      {items.length > 0 && (
        <div className="grid grid-cols-3 gap-2" data-testid="wa-summary">
          <Card className="p-3 rounded-xl border-stone-200"><div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{t("wa_reading")}</div><div className="font-mono text-2xl font-bold text-amber-600 mt-0.5" data-testid="wa-pending">{pendingCount}</div></Card>
          <Card className="p-3 rounded-xl border-stone-200"><div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{t("wa_ready")}</div><div className="font-mono text-2xl font-bold text-[#B45309] mt-0.5" data-testid="wa-ready">{readyCount}</div></Card>
          <Card className="p-3 rounded-xl border-stone-200"><div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{t("wa_drafted")}</div><div className="font-mono text-2xl font-bold text-[#166534] mt-0.5" data-testid="wa-drafted">{createdCount}</div></Card>
        </div>
      )}

      <div className="space-y-3">
        {items.map((item) => (
          <Card key={item.id} data-testid={`wa-item-${item.id}`}
                className={`rounded-2xl border overflow-hidden ${item.status === "created" ? "bg-green-50/50 border-green-200" : item.status === "error" ? "bg-red-50/40 border-red-200" : "border-stone-200"}`}>
            {/* header */}
            <div className="p-3 sm:p-4 flex items-center justify-between gap-2 border-b border-stone-100">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${item.status === "reading" ? "bg-amber-500 animate-pulse" : item.status === "ready" ? "bg-[#B45309]" : item.status === "created" ? "bg-[#166534]" : "bg-red-500"}`} />
                  <div className="font-medium text-sm truncate">{item.file_name}</div>
                </div>
                <div className="text-xs text-stone-500 mt-0.5">
                  {item.status === "reading" && t("wa_reading_msg")}
                  {item.status === "ready" && t("wa_ready_msg").replace("{n}", item.rows.length)}
                  {item.status === "created" && t("wa_created_msg")}
                  {item.status === "error" && `Error: ${item.error}`}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {item.status === "created" && (
                  <Button variant="ghost" size="sm" onClick={() => nav(`/bills/${item.bill_id}`)} data-testid={`wa-view-bill-${item.id}`} className="h-9 px-3 text-[#166534] text-xs font-semibold">
                    {t("open_bill")}
                  </Button>
                )}
                {item.status === "ready" && (
                  <button onClick={() => patch(item.id, { collapsed: !item.collapsed })} data-testid={`wa-toggle-${item.id}`} className="h-8 w-8 rounded-lg bg-stone-100 grid place-items-center text-stone-700">
                    {item.collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                  </button>
                )}
                <button onClick={() => removeItem(item.id)} data-testid={`wa-remove-${item.id}`} className="h-8 w-8 rounded-lg bg-red-50 grid place-items-center text-red-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {item.status === "reading" && (
              <div className="p-6 text-center">
                <Sparkles className="w-6 h-6 text-[#B45309] mx-auto mb-2 animate-pulse" />
                <div className="text-xs text-stone-500">Gemini 3.1 Pro</div>
              </div>
            )}

            {item.status === "ready" && !item.collapsed && (
              <div className="p-3 sm:p-4 space-y-3">
                {item.thumb_url && (
                  <a href={item.thumb_url} target="_blank" rel="noreferrer" className="block">
                    <img src={item.thumb_url} alt="Order" data-testid={`wa-thumb-${item.id}`}
                         className="w-full max-h-48 object-contain rounded-xl bg-stone-100" />
                  </a>
                )}
                <Input placeholder={t("customer")} value={item.customer} onChange={(e) => patch(item.id, { customer: e.target.value })} data-testid={`wa-customer-${item.id}`} className="h-11 rounded-lg" />

                {item.rows.length === 0 && <div className="text-sm text-stone-500 text-center py-4">{t("no_lines_detected")}</div>}

                <div className="space-y-2">
                  {item.rows.map((r, idx) => (
                    <div key={r._rid} data-testid={`wa-row-${item.id}-${idx}`}
                         className={`p-3 rounded-lg border ${r.cancelled ? "opacity-60 bg-stone-50" : r.needs_check ? "bg-amber-50/60 border-l-4 border-amber-500" : "border-stone-200"}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="text-xs text-stone-600 font-gujarati flex-1 min-w-0 truncate">{r.raw_text || "—"}</div>
                        <button onClick={() => removeRow(item.id, r._rid)} data-testid={`wa-row-del-${item.id}-${idx}`} className="h-7 w-7 rounded-md bg-red-50 grid place-items-center text-red-600 shrink-0">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">{t("name_en")} <span className="text-stone-400 normal-case">({t("optional")})</span></label>
                          <Input placeholder={t("name_en")} value={r.name_en || ""} onChange={(e) => setRowIn(item.id, r._rid, { name_en: e.target.value })} data-testid={`wa-en-${item.id}-${idx}`} className="h-10 rounded-lg text-sm" />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">{t("name_gu")}</label>
                          <Input placeholder={t("name_gu")} value={r.name_gu || ""} onChange={(e) => setRowIn(item.id, r._rid, { name_gu: e.target.value })} data-testid={`wa-gu-${item.id}-${idx}`} className="h-10 rounded-lg text-sm font-gujarati" />
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">{t("quantity")}</label>
                          <Input type="number" placeholder={t("quantity")} value={r.quantity ?? ""} onChange={(e) => setRowIn(item.id, r._rid, { quantity: parseFloat(e.target.value) || 0 })} data-testid={`wa-qty-${item.id}-${idx}`} className="h-10 rounded-lg font-mono text-sm w-full" />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">{t("unit")}</label>
                          <select value={r.unit || "pcs"} onChange={(e) => setRowIn(item.id, r._rid, { unit: e.target.value })} data-testid={`wa-unit-${item.id}-${idx}`} className="h-10 w-full rounded-lg border border-stone-300 px-2 text-sm">
                            {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">{t("rate")}</label>
                          <Input type="number" placeholder={t("rate")} value={r.rate ?? 0} onChange={(e) => setRowIn(item.id, r._rid, { rate: parseFloat(e.target.value) || 0 })} data-testid={`wa-rate-${item.id}-${idx}`} className="h-10 rounded-lg font-mono text-sm w-full" />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">{t("amount")}</label>
                          <div className="h-10 rounded-lg bg-stone-100 grid place-items-center font-mono text-sm font-semibold">₹{((r.quantity || 0) * (r.rate || 0)).toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <Button onClick={() => createDraft(item)} data-testid={`wa-draft-${item.id}`}
                        className="w-full h-12 rounded-xl bg-[#166534] hover:bg-[#14532D] text-white font-semibold">
                  <Save className="w-4 h-4 mr-2" /> {t("create_draft_bill")}
                </Button>
              </div>
            )}

            {item.status === "error" && (
              <div className="p-4 flex items-center gap-2 text-red-700 text-sm">
                <AlertTriangle className="w-4 h-4 shrink-0" /> {item.error}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
